export type CreativeRole =
  | 'background'
  | 'hero'
  | 'logo'
  | 'headline'
  | 'body'
  | 'cta'
  | 'legal'
  | 'accent';

export type CanvasClass =
  | 'ribbon'
  | 'skyscraper'
  | 'panel_wide'
  | 'panel_square'
  | 'panel_tall';

export type MinReadiness =
  | 'composite'
  | 'text_lifted'
  | 'subject_separated'
  | 'extensible';

export type ConstraintKind =
  | 'must_appear'
  | 'never_crop'
  | 'full_bleed'
  | 'pill'
  | 'min_legible'
  | 'contrast_ratio'
  | 'safe_area'
  | 'casts_shadow'
  | 'adjacent';

export interface TemplateConstraint {
  kind: ConstraintKind;
  role?: string;
  role_a?: string;
  role_b?: string;
  px?: number;
  min_ratio?: number;
  margin_frac?: number;
}

export interface TemplateRoleSpec {
  role: CreativeRole;
  required: boolean;
  min_readiness: MinReadiness;
  constraints: TemplateConstraint[];
  drop_rank: number | null;
}

export interface TemplateZone {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface BreakpointSpec {
  canvas_class: CanvasClass;
  zones: Record<string, TemplateZone>;
  max_text_roles: number;
  min_hero_area_frac: number;
}

export interface FallbackSpec {
  drop_roles: string[];
  promote_roles: string[];
  background: 'brand_solid' | 'brand_gradient' | 'sampled_solid';
}

export interface TemplateGenerationPolicy {
  level: 'none' | 'background_only' | 'full';
  max_generated_area_frac: number;
  require_provenance: boolean;
}

export interface ComplianceSpec {
  requires_disclaimer: boolean;
  min_legal_font_px: number;
  disclaimer_role: string;
}

export interface ArtDirectionSpec {
  type_pairing: string | null;
  palette_strategy: 'brand_exact' | 'brand_derived' | 'sampled';
}

export interface CreativeTemplatePayload {
  template_id: string;
  version: number;
  name: string;
  roles: TemplateRoleSpec[];
  breakpoints: BreakpointSpec[];
  fallback: FallbackSpec;
  generation_policy: TemplateGenerationPolicy;
  compliance: ComplianceSpec;
  art_direction: ArtDirectionSpec;
}

export interface CreativeTemplate {
  id: string;
  template_id: string;
  version: number | null;
  version_id: string | null;
  payload: CreativeTemplatePayload;
  created_at: string;
  updated_at: string;
}

export interface CreativeTemplateVersionSummary {
  id: string;
  version: number;
  created_by: string;
  created_by_email: string;
  created_at: string;
  change_note: string;
}

export interface CreativeTemplateVersionDetail extends CreativeTemplateVersionSummary {
  payload: CreativeTemplatePayload;
}

export interface CreativeTemplateWritePayload {
  template_id?: string;
  name?: string;
  roles?: TemplateRoleSpec[];
  breakpoints?: BreakpointSpec[];
  fallback?: FallbackSpec;
  generation_policy?: Partial<TemplateGenerationPolicy>;
  compliance?: Partial<ComplianceSpec>;
  art_direction?: Partial<ArtDirectionSpec>;
  change_note?: string;
}

export const CREATIVE_ROLE_OPTIONS: CreativeRole[] = [
  'background',
  'hero',
  'logo',
  'headline',
  'body',
  'cta',
  'legal',
  'accent',
];

export const CANVAS_CLASS_OPTIONS: CanvasClass[] = [
  'ribbon',
  'skyscraper',
  'panel_wide',
  'panel_square',
  'panel_tall',
];

export const MIN_READINESS_OPTIONS: MinReadiness[] = [
  'composite',
  'text_lifted',
  'subject_separated',
  'extensible',
];

export const CONSTRAINT_KIND_OPTIONS: ConstraintKind[] = [
  'must_appear',
  'never_crop',
  'full_bleed',
  'pill',
  'min_legible',
  'contrast_ratio',
  'safe_area',
  'casts_shadow',
  'adjacent',
];
